import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import Seo from "@/components/Seo";

const POLL_INTERVAL_MS = 3000;
const FAST_POLL_DURATION_MS = 30_000;   // First 30s: 3s polling
const SLOW_POLL_INTERVAL_MS = 10_000;   // After 30s: 10s polling
const HARD_STOP_AFTER_MS = 15 * 60_000; // Stop polling after 15 minutes

function Spinner({ size = 16 }) {
  return <Lucide.Loader2 className="animate-spin" size={size} />;
}

export default function UPIThanks() {
  const { refId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());
  const timer = useRef(null);
  const elapsedTimer = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get(`/upi/payment-status/${encodeURIComponent(refId)}`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      // 404 in the first ~2 minutes is fine — log just got created and Mongo
      // index hasn't propagated, OR the user typed a wrong ref. Treat both as
      // "still waiting" until 2 min, then surface the error.
      const since = Date.now() - startedAt.current;
      if (e?.response?.status === 404 && since < 120_000) {
        setData(null);
        setErr(null);
      } else {
        setErr(e?.response?.data?.detail || "Could not fetch payment status");
      }
    }
  };

  useEffect(() => {
    fetchStatus();
    elapsedTimer.current = setInterval(() => setElapsed(Date.now() - startedAt.current), 1000);

    const scheduleNext = () => {
      const since = Date.now() - startedAt.current;
      if (since > HARD_STOP_AFTER_MS) return;       // give up polling
      const interval = since < FAST_POLL_DURATION_MS ? POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
      timer.current = setTimeout(async () => {
        await fetchStatus();
        // Stop once we hit a terminal state
        if (data?.payment_status === "success" || data?.payment_status === "failed" || data?.payment_status === "cancelled") return;
        scheduleNext();
      }, interval);
    };
    scheduleNext();

    return () => {
      clearTimeout(timer.current);
      clearInterval(elapsedTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  // Stop polling once confirmed
  useEffect(() => {
    if (data?.payment_status === "success" || data?.payment_status === "failed" || data?.payment_status === "cancelled") {
      clearTimeout(timer.current);
    }
  }, [data?.payment_status]);

  const seconds = Math.floor(elapsed / 1000);
  const stoppedPolling = elapsed > HARD_STOP_AFTER_MS;

  // ─── Confirmed ─────────────────────────────────────────────────────────
  if (data?.payment_status === "success") {
    const amt = Number(data.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
    return (
      <Shell>
        <Seo title={`Payment confirmed · ${refId}`} description="Your UPI payment has been confirmed by BINAREX AND CO." />
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
          className="mx-auto w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.25) 0%, transparent 70%)" }}
          data-testid="thanks-success"
        >
          <Lucide.CheckCircle2 size={56} className="text-emerald-300" strokeWidth={1.8} />
        </motion.div>
        <h1 className="bx-heading text-2xl font-extrabold mt-5">Payment confirmed</h1>
        <p className="text-slate-400 text-sm mt-2">
          Thanks{data.customer_name ? `, ${data.customer_name.split(" ")[0]}` : ""}! We received <strong className="text-purple-300">₹{amt}</strong>.
          {data.pay_link_label && <span className="block mt-1 text-slate-500 text-xs">for {data.pay_link_label}</span>}
        </p>
        <Details data={data} />
        <p className="text-[10px] text-slate-500 mt-6">Your receipt + PDF invoice will arrive in your email shortly.</p>
      </Shell>
    );
  }

  // ─── Failed / Cancelled ────────────────────────────────────────────────
  if (data?.payment_status === "failed" || data?.payment_status === "cancelled") {
    return (
      <Shell>
        <Seo title={`Payment ${data.payment_status} · ${refId}`} description="Payment status update from BINAREX AND CO." />
        <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle, rgba(244,63,94,0.25) 0%, transparent 70%)" }}>
          <Lucide.XCircle size={56} className="text-rose-300" strokeWidth={1.8} />
        </div>
        <h1 className="bx-heading text-2xl font-extrabold mt-5">Payment {data.payment_status}</h1>
        <p className="text-slate-400 text-sm mt-2">This payment didn't go through. If money was deducted from your bank, it'll be refunded automatically within 5–7 working days.</p>
        <Details data={data} />
        <p className="text-[10px] text-slate-500 mt-6">Need help? Reply to your payment-link email or reach <span className="text-purple-300">support@binarex.co</span> — please quote reference <strong>{refId}</strong>.</p>
      </Shell>
    );
  }

  // ─── Not found / wrong ref ─────────────────────────────────────────────
  if (err) {
    return (
      <Shell>
        <Lucide.AlertOctagon size={40} className="text-amber-300 mx-auto" />
        <h1 className="bx-heading text-2xl font-extrabold mt-4">Can't find this payment</h1>
        <p className="text-slate-400 text-sm mt-2">{err}</p>
        <p className="text-[11px] text-slate-500 mt-4">Reference: <span className="bx-mono text-purple-300">{refId}</span></p>
      </Shell>
    );
  }

  // ─── Pending — still polling ───────────────────────────────────────────
  return (
    <Shell>
      <Seo title={`Waiting for payment · ${refId}`} description="We're confirming your UPI payment." />
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="mx-auto w-20 h-20 rounded-full flex items-center justify-center"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.25) 0%, transparent 70%)" }}
        data-testid="thanks-pending"
      >
        <Lucide.Loader2 size={48} className="text-purple-300 animate-spin" strokeWidth={1.5} />
      </motion.div>
      <h1 className="bx-heading text-2xl font-extrabold mt-5">
        {data ? "Confirming your payment…" : "Looking up your payment…"}
      </h1>
      <p className="text-slate-400 text-sm mt-2">
        {stoppedPolling
          ? "We've stopped automatic checks. If you completed the payment, please refresh in a few minutes — or contact support."
          : "Most UPI payments confirm in under 30 seconds. We'll update this page automatically the moment it lands."}
      </p>
      {data && <Details data={data} />}
      <div className="mt-5 text-[11px] text-slate-500 flex items-center justify-center gap-2">
        <Spinner size={11} /> Waiting · {Math.max(seconds, 1)}s elapsed
      </div>
      <button
        onClick={() => { startedAt.current = Date.now(); fetchStatus(); }}
        className="bx-btn-ghost text-xs mt-4"
        data-testid="thanks-refresh"
      >
        <Lucide.RefreshCw size={11} /> Refresh now
      </button>
    </Shell>
  );
}

function Details({ data }) {
  if (!data) return null;
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 mt-5 text-left max-w-xs mx-auto" data-testid="thanks-details">
      <Row label="Amount" value={data.amount ? `₹${Number(data.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"} mono />
      <Row label="Reference" value={data.merchant_reference_id} mono accent />
      {data.upi_transaction_id && <Row label="UPI Txn" value={data.upi_transaction_id} mono />}
      {data.receiver_upi_id && <Row label="Paid to" value={data.receiver_upi_id} mono />}
      <Row label="When" value={data.paid_at ? new Date(data.paid_at).toLocaleString() : (data.created_at ? new Date(data.created_at).toLocaleString() : "—")} />
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-xs text-right break-all ${mono ? "bx-mono" : ""} ${accent ? "text-purple-300" : "text-slate-200"}`}>{value || "—"}</span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#05050A] text-slate-100 px-5 flex items-center justify-center" data-testid="thanks-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="bx-card p-8 max-w-md w-full text-center relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.10) 0%, rgba(5,5,10,1) 100%)" }}
      >
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-5">BINAREX AND CO · UPI</div>
        {children}
      </motion.div>
    </div>
  );
}
